import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';

import { useCuration } from '@hooks/useCuration';
import { useDeleteRecord } from '@hooks/useDeleteRecord';
import { useRecordFlags } from '@hooks/useRecordFlags';
import { useSnackbar } from '@hooks/useSnackbar';

const messages = defineMessages({
  deleteConfirm: {
    id: 'drilldownHeader.deleteConfirm',
    defaultMessage: 'Are you sure you want to delete this record?',
  },
  permanent: {
    id: 'drilldownHeader.permanent',
    defaultMessage:
      'This cannot be undone. All tasks, associations, and references to another resource will be deleted as well.',
  },
  deletePhrase: {
    id: 'confirmation.deletePhrase',
    defaultMessage: 'DELETE',
  },
  deleteConfirmation: {
    id: 'GLOBAL.deleteConfirmation',
    defaultMessage: 'To confirm deletion, type {phrase} in the field.',
  },
  deleteRecord: {
    id: 'GLOBAL.deleteRecord',
    defaultMessage:
      'There was an error with deleting a record. Please try again later.',
  },
});

export default function CurationDrilldownDeleteButton() {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const { deleteRecord } = useDeleteRecord();
  const {
    state: { isEditing, record },
  } = useCuration();
  const { canDelete: canUserDeleteRecord } = useRecordFlags();

  if (!record) {
    return null;
  }

  return (
    <>
      {!isEditing && canUserDeleteRecord && (
        <ConfirmationDialog
          challenge={{
            description: formatMessage(messages.deleteConfirmation, {
              phrase: formatMessage(messages.deletePhrase),
            }),
            phrase: formatMessage(messages.deletePhrase),
          }}
          content={formatMessage(messages.permanent)}
          onConfirm={async () => {
            const deleted = await deleteRecord(record);
            if (!deleted) {
              enqueueSnackbar({
                message: formatMessage(messages.deleteRecord),
                variant: 'error',
              });
            }
          }}
          title={formatMessage(messages.deleteConfirm)}
        >
          {({ handleOpen }) => (
            <Icon
              clickable
              color="primary.contrastText"
              name="trash"
              onClick={handleOpen}
              type="fas"
            />
          )}
        </ConfirmationDialog>
      )}
    </>
  );
}
